// External Dependencies:
import Cookie from 'js-cookie'

// Internal Dependencies:
import { getUserPreferredLocale } from 'church-theme-library/utilities/i18n'
import { getLastDayOfWeekYmd, getNextDayOfWeekYmd } from 'church-theme-library/utilities/date'
import { get } from 'church-theme-library/utilities/objects'

let userIsLoggedIn = siteData.is_user_logged_in ? true : false
let wpRestNonce = siteData.wp_rest_nonce
const inGdprZone = true // Assume GDPR, avoiding location lookups.
const cookieConsent = Cookie.get( 'cookies_consent' )
const announcementBarDismissed = Cookie.get( 'announcement_bar_dismissed' )
const locale = getUserPreferredLocale( siteData.locales_slugs, siteData.locale_base )
const nativeConnection = !! window.navigator.connection
const lastSatDateYmd = getLastDayOfWeekYmd( 6 )
const nextSatDateYmd = getNextDayOfWeekYmd( 6 )

// Overrides when using static cache:
if ( typeof vlSiteData !== 'undefined' || typeof vlPageData !== 'undefined' ) {
	userIsLoggedIn = false
	wpRestNonce = false
}

const hasMultipleLocales = siteData.locales && siteData.locales.length > 1 ? true : false

// Slugs:
let slugs = siteData.locales_page_slugs[ locale ]

if ( ! slugs ) {
	slugs = siteData.locales_page_slugs[ Object.keys( siteData.locales_page_slugs)[0] ]
}

// Social:
let facebookURL = siteData.facebook_url.replace( /\/$/, '' ) + '/' // Ensure trailing slash.

// Images:
let loadedImages = localStorage.getItem( 'loadedImages' )

if ( loadedImages ) {
	loadedImages = JSON.parse( loadedImages )
} else {
	loadedImages = []
}

// Announcement bar:
let announcementBarEnable = siteData.announcement_bar_enable

if ( announcementBarDismissed ) {
	announcementBarEnable = false
}

const initialState = {
	// Environment:
	screenWidth: window.innerWidth,
	screenHeight: window.innerHeight,
	isMobile: window.innerWidth <= siteData.mobile_max_width,

	// User:
	userIsLoggedIn: userIsLoggedIn,
	wpRestNonce: wpRestNonce,
	inGdprZone: inGdprZone,
	cookieConsent: cookieConsent,
	locale: locale,
	locales: siteData.locales,
	hasMultipleLocales: hasMultipleLocales,

	// Connection:
	connection: {
		onLine: true,
		possiblySlowNetwork: false, // Guessed connection speed when nativeConnection is not available.
		downlink: nativeConnection ? navigator.connection.downlink : false, // Megabits per second.
		rtt: nativeConnection ? navigator.connection.rtt : false, // Ms.
		effectiveType: nativeConnection ? navigator.connection.effectiveType : false, // 'slow-2g', '2g', '3g', or '4g'.
	},

	// Scripts:
	loadedScripts: [],

	// Images:
	loadedImages: loadedImages,

	// Date:
	currentYear: new Date().getFullYear(),

	// Site:
	homeUrl: '/',
	slugs,
	menuIsOpen: false,
	isLoadingMenu: false,
	menuItems: siteData.site_menu,
	socialItems: siteData.site_social,
	menuOverflowItems: [],
	localeMenuIsOpen: false,
	siteName: siteData.site_name,
	siteNameR: siteData.site_name_r,
	siteShortName: siteData.site_short_name,
	siteFullName: siteData.site_full_name,
	locationAddress: siteData.location_address,
	locationMapImage: siteData.location_map_image,
	locationLat: siteData.location_lat,
	locationLng: siteData.location_lng,
	locationURLPart: siteData.location_url_part,
	phone: siteData.phone,
	customLogoUrl: siteData.custom_logo_url,
	facebookURL: facebookURL,
	facebookPageID: siteData.facebook_page_id,
	facebookCustomerChatEnable: siteData.facebook_customer_chat_enable,
	youtubeURL: siteData.youtube_url,
	instagramURL: siteData.instagram_url,
	twitterURL: siteData.twitter_url,
	eventsCalendarType: siteData.events_calendar_type,

	announcementBarEnable: announcementBarEnable,
	announcementBarDismissable: siteData.announcement_bar_dismissable,
	announcementBarText: siteData.announcement_bar_text,
	announcementBarButtonText: siteData.announcement_bar_button_text,
	announcementBarButtonURL: siteData.announcement_bar_button_url,

	videos: false,
	videosReceived: false,
	livestream: false,
	livestreamReceived: false,
	sabbathSchoolThisWeek: false,
	sabbathSchoolThisWeekByLocale: {},
	sabbathSchoolDataReceived: false,

	// Login:
	loginIsDisabled: false,
	loginIsProcessing: false,
	loginIsProcessed: false,
	loginError: false,
	loginSuccess: false,
	loginMessage: false
}

export default function pageReducer( state = initialState, action ) {
	const { type, payload } = action

	switch ( type ) {
		case 'GET_SCREEN_SIZE':
			return {
				...state,
				screenWidth: window.innerWidth,
				screenHeight: window.innerHeight,
				isMobile: window.innerWidth <= siteData.mobile_max_width
			}

		case 'ADD_SCRIPT':
			return {
				...state,
				loadedScripts: [
					...state.loadedScripts,
					payload
				]
			}

		case 'SET_COOKIE_CONSENT':
			return {
				...state,
				cookieConsent: payload
			}

		case 'SET_ONLINE_STATUS':
			return {
				...state,
				connection: {
					...state.connection,
					onLine: payload
				}
			}

		case 'SET_CONNECTION_STATUS':
			return {
				...state,
				connection: {
					...state.connection,
					...payload
				}
			}

		case 'REGISTER_IMAGE_ID_AS_LOADED':
			return {
				...state,
				loadedImages: payload
			}

		case 'SET_LOCALE':
			let slugs = siteData.locales_page_slugs[ payload ]

			if ( ! slugs ) {
				slugs = siteData.locales_page_slugs[ Object.keys( siteData.locales_page_slugs)[0] ]
			}

			return {
				...state,
				locale: payload,
				slugs
			}

		case 'DISMISS_ANNOUNCEMENT_BAR':
			return {
				...state,
				announcementBarEnable: false
			}

		case 'LOGIN':
			return {
				...state,
				userIsLoggedIn: true,
				loginError: false
			}

		case 'LOGIN_DISABLED':
			return {
				...state,
				loginIsDisabled: payload,
				loginError: false,
				loginSuccess: false
			}

		case 'LOGIN_PROCESSING':
			return {
				...state,
				loginIsProcessing: true,
				loginIsProcessed: false,
				loginError: false,
				loginSuccess: false
			}

		case 'LOGIN_PROCESSED':
			return {
				...state,
				loginIsProcessed: true,
				loginIsProcessing: false
			}

		case 'LOGIN_ERROR':
			return {
				...state,
				loginError: true,
				loginIsProcessed: false
			}

		case 'LOGIN_SUCCESS':
			return {
				...state,
				loginSuccess: true,
				loginIsProcessed: false
			}

		case 'LOGIN_MESSAGE':
			return {
				...state,
				loginMessage: payload
			}

		case 'LOGOUT':
			return {
				...state,
				userIsLoggedIn: false
			}

		case 'OPEN_MENU':
			return {
				...state,
				menuIsOpen: true
			}

		case 'CLOSE_MENU':
			return {
				...state,
				menuIsOpen: false
			}

		case 'SET_MENU_OPEN_STATE':
			return {
				...state,
				menuIsOpen: payload
			}

		case 'SET_LOCALE_MENU_OPEN_STATE':
			return {
				...state,
				localeMenuIsOpen: payload
			}

		case 'REQUEST_MENU_DATA':
			return {
				...state,
				isLoadingMenu: true
			}

		case 'RECEIVE_MENU_DATA':
			return {
				...state,
				isLoadingMenu: false,
				menuItems: payload.site_menu,
				socialItems: payload.site_social
			}

		case 'SET_MENU_OVERFLOW_ITEMS':
			return {
				...state,
				menuOverflowItems: payload
			}

		case 'RECEIVE_SITE_DATA':
			let preachersByWeekAll = get( [ 'preachers_by_week', 'all' ] )( payload, false )
			let preacherThisWeek = get( [ nextSatDateYmd ] )( preachersByWeekAll, false )

			if ( ! preacherThisWeek ) {
				preacherThisWeek = get( [ 'preachers_by_week', 'default' ] )( payload, {} )
			}

			return {
				...state,
				menuItems: payload.site_menu,
				socialItems: payload.site_social,
				siteName: payload.site_name,
				siteNameR: payload.site_name_r,
				siteShortName: payload.site_short_name,
				siteFullName: payload.site_full_name,
				locationAddress: payload.location_address,
				locationMapImage: payload.location_map_image,
				locationLat: payload.location_lat,
				locationLng: payload.location_lng,
				locationURLPart: payload.location_url_part,
				phone: payload.phone,
				customLogoUrl: payload.custom_logo_url,
				preachersByWeek: preachersByWeekAll,
				preacherThisWeek: preacherThisWeek,
				customLogoUrl: payload.custom_logo_url,
				facebookURL: payload.facebook_url,
				facebookPageID: payload.facebook_page_id,
				facebookCustomerChatEnable: payload.facebook_customer_chat_enable,
				youtubeURL: payload.youtube_url,
				instagramURL: payload.instagram_url,
				twitterURL: payload.twitter_url
			}

		case 'RECEIVE_SABBATH_SCHOOL_DATA':
			let ssDataByWeekAll = get( [ 'by_sat' ] )( payload, false )
			let sabbathSchoolThisWeek = get( [ lastSatDateYmd ] )( ssDataByWeekAll, {} )

			return {
				...state,
				sabbathSchoolDataReceived: true,
				sabbathSchoolThisWeek: sabbathSchoolThisWeek,
				sabbathSchoolThisWeekByLocale: {
					...state.sabbathSchoolThisWeekByLocale,
					[ state.locale ]: sabbathSchoolThisWeek
				}
			}

		case 'RECEIVE_SABBATH_SCHOOL_DATA_SINGLE':
			return {
				...state,
				sabbathSchoolDataReceived: true,
				sabbathSchoolThisWeek: payload
			}

		case 'RECEIVE_VIDEOS_DATA':
			return {
				...state,
				videos: payload,
				videosReceived: true
			}

		case 'RECEIVE_LIVESTREAM_DATA':
			return {
				...state,
				livestream: payload,
				livestreamReceived: true
			}
	}

	return state
}
